if (!customElements.get('subscription-purchase-selector')) {
  customElements.define(
    'subscription-purchase-selector',
    class SubscriptionPurchaseSelector extends HTMLElement {
      connectedCallback() {
        this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
        this.sectionId = this.dataset.sectionId || '';
        this.variantData = {};
        try {
          const el = this.querySelector('script[type="application/json"]');
          if (el && el.textContent.trim()) {
            const raw = JSON.parse(el.textContent);
            this.variantData = raw.variants || raw;
          }
        } catch (e) {
          console.warn('subscription-purchase-selector: invalid JSON', e);
        }

        this.subRadio = this.querySelector('[data-subscription-radio]');
        this.oneRadio = this.querySelector('[data-one-time-radio]');
        this.subLabel = this.querySelector('[data-subscription-label]');
        this.plansWrapper = this.querySelector('[data-subscription-plans]');
        this.planSelect = this.querySelector('[data-selling-plan-select]');
        this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
        this.infoButton = this.querySelector('.subscription-purchase__text--linked');
        this._preferredPlanId = this.planSelect?.value || '';

        this.infoButton?.addEventListener('click', () => {
          if (this.subRadio?.disabled) return;
          this.subRadio.checked = true;
          this.onPurchaseOptionChange();
        });

        this._onVariantChange = () => {
          this.onVariantChange();
          this.schedulePriceResync();
        };
        this.variantInput = this.form?.querySelector('input[name="id"]');
        this.variantInput?.addEventListener('change', this._onVariantChange);
        this.variantInput?.addEventListener('input', this._onVariantChange);

        this.addEventListener('change', (e) => {
          if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement)) return;

          if (e.target.name === 'purchase_option') {
            this.onPurchaseOptionChange();
          } else if (e.target.matches('[data-selling-plan-select]')) {
            this._preferredPlanId = e.target.value;
            this.syncSellingPlanInput();
            this.updatePriceDisplay();
          }
        });

        this._defaultPriceHTML = '';
        this.captureDefaultPrice();
        this.onVariantChange();
        this.onPurchaseOptionChange();
        this.updatePriceDisplay();
      }

      disconnectedCallback() {
        clearTimeout(this._priceResyncTimer);
        this.variantInput?.removeEventListener('change', this._onVariantChange);
        this.variantInput?.removeEventListener('input', this._onVariantChange);
      }

      priceEl() {
        return this.sectionId ? document.getElementById(`price-${this.sectionId}`) : null;
      }

      captureDefaultPrice() {
        const el = this.priceEl();
        if (el && !el.hasAttribute('data-subscription-price')) {
          this._defaultPriceHTML = el.innerHTML;
        }
      }

      schedulePriceResync() {
        clearTimeout(this._priceResyncTimer);
        this._priceResyncTimer = setTimeout(() => {
          this.captureDefaultPrice();
          this.updatePriceDisplay();
        }, 150);
      }

      getSelectedPlan(row) {
        if (!row?.plans?.length || !this.planSelect) return null;
        const selectedId = String(this.planSelect.value);
        return row.plans.find((plan) => String(plan.id) === selectedId) || row.plans[0];
      }

      rebuildPlanSelect(plans) {
        if (!this.planSelect) return;

        const previousId = this._preferredPlanId || this.planSelect.value;
        this.planSelect.innerHTML = '';

        plans.forEach((plan) => {
          const option = document.createElement('option');
          option.value = String(plan.id);
          option.textContent = plan.name;
          this.planSelect.appendChild(option);
        });

        const matchingOption = Array.from(this.planSelect.options).find(
          (option) => option.value === String(previousId)
        );
        if (matchingOption) {
          this.planSelect.value = matchingOption.value;
        }

        this._preferredPlanId = this.planSelect.value;
      }

      syncSellingPlanInput() {
        if (!this.sellingPlanInput) return;

        if (this.subRadio?.checked && this.planSelect?.value) {
          this.sellingPlanInput.value = this.planSelect.value;
        } else {
          this.sellingPlanInput.value = '';
        }
      }

      updatePlansVisibility() {
        if (!this.plansWrapper) return;
        const showPlans = Boolean(this.subRadio?.checked && !this.subRadio.disabled);
        this.plansWrapper.hidden = !showPlans;
      }

      onPurchaseOptionChange() {
        this.updatePlansVisibility();
        this.syncSellingPlanInput();
        this.updatePriceDisplay();
      }

      onVariantChange() {
        if (!this.variantInput || !this.subRadio) return;
        const vid = String(this.variantInput.value);
        const row = this.variantData[vid];
        const plans = row?.plans || [];
        const hasPlans = plans.length > 0;

        if (hasPlans) {
          this.subRadio.disabled = false;
          this.subRadio.removeAttribute('aria-disabled');
          this.subLabel?.classList.remove('subscription-purchase__option--disabled');
          this.planSelect?.removeAttribute('disabled');
          this.rebuildPlanSelect(plans);
        } else {
          this.subRadio.disabled = true;
          this.subRadio.setAttribute('aria-disabled', 'true');
          this.subLabel?.classList.add('subscription-purchase__option--disabled');
          this.planSelect?.setAttribute('disabled', 'disabled');
          if (this.planSelect) this.planSelect.innerHTML = '';
          if (this.subRadio.checked && this.oneRadio) {
            this.oneRadio.checked = true;
          }
        }

        this.onPurchaseOptionChange();
      }

      updatePriceDisplay() {
        const priceEl = this.priceEl();
        if (!priceEl || !this.variantInput) return;

        const vid = String(this.variantInput.value);
        const row = this.variantData[vid];
        const selectedPlan = this.getSelectedPlan(row);
        const showSubPrice = Boolean(this.subRadio?.checked && selectedPlan);

        if (showSubPrice) {
          priceEl.setAttribute('data-subscription-price', 'true');
          priceEl.innerHTML = this.buildSubscriptionPriceHTML(row.oneTime, selectedPlan.price);
          return;
        }

        priceEl.removeAttribute('data-subscription-price');
        if (this._defaultPriceHTML) {
          priceEl.innerHTML = this._defaultPriceHTML;
        }
      }

      buildSubscriptionPriceHTML(oneTime, subscription) {
        const safe = (s) =>
          String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
        return `<div class="price price--large subscription-purchase-price" data-subscription-price-layout>
    <div class="price__container">
      <div class="price__sale">
        <span class="visually-hidden visually-hidden--inline">Price</span>
        <span class="price-item price-item--sale price-item--last h4">${safe(subscription)}</span>
      </div>
    </div>
  </div>`;
      }
    }
  );
}
